import React, { useContext, useEffect } from "react";
import dayjs from "dayjs";
import { Feed, Icon, List, Message } from "semantic-ui-react";
import { IRecentCall } from "../models/IRecentCall";
import AppContext from "../contexts/AppContextProvider";
import { getLogger } from "../services/LoggingService";
import { CallContext } from "../contexts/CallContext";
import { useCallLogState } from "../hooks/useCallLogState";

const logger = getLogger("RecentCalls");

export function RecentCalls() {
    const { calls, setCalls } = useCallLogState();

    const { setAlert } = useContext(AppContext);
    const { contact } = useContext(CallContext);

    const onConnected = (c: connect.Contact) => {
        try {
            const exists = calls.current.find((x) => x.contactId === c.getContactId());

            if (exists) {
                return;
            }
            const currentCall: IRecentCall = {
                contactId: c.getContactId(),
                phoneNumber: c.getInitialConnection()?.getEndpoint().phoneNumber,
                disposition: c.getAttributes().disposition?.value || "",
                customerName: c.getAttributes().customerName?.value || "",
                brandCode: c.getAttributes().brandCode?.value || ("" as any),
                startTimestamp: new Date().getTime(),
            };
            setCalls([currentCall, ...calls.current]);
        } catch (error) {
            logger.error(error);
        }
    };

    const onRefresh = (c: connect.Contact) => {
        if (!calls || calls.current.length === 0) {
            return;
        }

        const disposition = c.getAttributes().disposition?.value || "";
        const exists = calls.current.find((x) => x.contactId === c.getContactId());
        if (exists && exists.disposition !== disposition) {
            exists.disposition = disposition;
            setCalls([...calls.current]);
        }
    };

    const onEnded = (c: connect.Contact) => {
        if (!calls || calls.current.length === 0) {
            return;
        }

        const exists = calls.current.find((x) => x.contactId === c.getContactId());
        if (exists && !exists.endTimestamp) {
            exists.endTimestamp = new Date().getTime();
            exists.disposition = c.getAttributes().disposition?.value || "";
            setCalls([...calls.current]);
        }
    };

    useEffect(() => {
        if (!contact) return;
        contact.onConnected(onConnected);
        contact.onRefresh(onRefresh);
        contact.onEnded(onEnded);
        //contact.onAccepted(onAccepted)
    }, [contact]);

    const clickToCall = (phoneNumber: string) => {
        const endpoint = connect.Endpoint.byPhoneNumber(phoneNumber);
        connect.agent((agent: connect.Agent) => {
            agent.connect(endpoint, {
                success: () => {
                    // Do nothing
                },
                failure: (e) => {
                    logger.error("RecentCalls.ClickToCall:Error", e);
                    setAlert("Call Failed", "error");
                },
            });
        });
    };

    const millisecondsToHuman = (ms: number) => {
        let seconds: any = Math.floor((ms / 1000) % 60);
        let minutes: any = Math.floor((ms / 1000 / 60) % 60);
        let hours: any = Math.floor((ms / 1000 / 3600) % 24);

        hours = hours < 10 ? "0" + hours : hours;
        minutes = minutes < 10 ? "0" + minutes : minutes;
        seconds = seconds < 10 ? "0" + seconds : seconds;
        return hours + ":" + minutes + ":" + seconds;
    };

    const renderCall = (call: IRecentCall) => {
        return (
            <Feed key={call.contactId}>
                <Feed.Event>
                    <Feed.Label className="callIconBox">
                        <Icon onClick={() => clickToCall(call.phoneNumber)} name="call" className="call-icon" />
                    </Feed.Label>
                    <Feed.Content>
                        <Feed.Summary>
                            <Feed.User>{call.customerName}</Feed.User>&nbsp;
                            {call.phoneNumber}
                        </Feed.Summary>
                        <Feed.Meta>
                            <Feed.Like>
                                {dayjs(call.startTimestamp).format("MMM DD - hh:mm A")} &nbsp;
                                <Icon name="clock outline" />
                                &nbsp;
                                {millisecondsToHuman((call.endTimestamp || call.startTimestamp) - call.startTimestamp)}
                            </Feed.Like>
                        </Feed.Meta>
                        {call.disposition && (
                            <Feed.Meta>
                                <Feed.Like>
                                    &nbsp;|&nbsp;<b>Disposition:</b> {call.disposition ? call.disposition : ""}
                                </Feed.Like>
                            </Feed.Meta>
                        )}
                    </Feed.Content>
                </Feed.Event>
            </Feed>
        );
    };

    if (calls.current.length === 0) {
        return <Message warning content="No recent calls to display." />;
    }

    return (
        <List divided relaxed>
            {calls.current.map((x) => renderCall(x))}
        </List>
    );
}
